import { c as _c } from "react/compiler-runtime";
import { ALPHAMD_SENDER_ID } from "@/services/alphaMdMessages";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import { convertTo24HourWithDateFormat } from "../../../utils/common";
import socketService from "../../../utils/socket";
import useAppointments from "../../../reactQuery/appointment";
import { useDispatch, useSelector } from "react-redux";
import { removeNotification } from "../../../Redux/slice/notificationSlice";
import { toast } from "react-toastify";
import { useRouter } from "next/navigation";
import Avatar from "react-avatar";

/**
 * Custom renderer for the daily "Pipeline Hot List" notification.
 *
 * Mirrors the Emerging & Pipeline row layout so the notification reads
 * the same way the doctor would see it on the Discover surface:
 *
 *   [#1] Drug name  [PHASE 3]                   [↑ 400% / week]
 *        Indication
 *        Sponsor · 240 patients · completes Aug 2026
 *        Italic mechanism preview...
 *
 * All enriched fields (sponsor / trend / mechanism / completion /
 * enrollment) are optional — when an older notification doc lacks
 * them, the row gracefully falls back to just name + phase + indication.
 */
/**
 * Identical to the SinceLastWeek widget rendered on the Pipeline Pulse
 * dashboard card (PipelinePulseCard.jsx). Same SVG glyphs + same color
 * rules (grey for zero, green for up, red for down), so the
 * notification card and the dashboard row read as one design language.
 */
function SinceLastWeekWidget(t0) {
  const $ = _c(9);
  const {
    pct
  } = t0;
  const value = Number.isFinite(pct) ? pct : 0;
  const color = value === 0 ? "text-neutral-400" : value > 0 ? "text-green-600" : "text-[#D53033]";
  const t1 = `${color} text-xs font-medium font-poppins flex items-center gap-2`;
  let t2;
  if ($[0] !== value) {
    t2 = <span className="w-4 h-4 relative flex items-center justify-center">{value === 0 ? <svg xmlns="http://www.w3.org/2000/svg" width="12" height="2" viewBox="0 0 12 2" fill="none"><path d="M1.33341 0.333313C0.965223 0.333313 0.666748 0.631766 0.666748 0.99998C0.666748 1.36819 0.965223 1.66665 1.33341 1.66665H10.6667C11.035 1.66665 11.3334 1.36819 11.3334 0.99998C11.3334 0.631766 11.035 0.333313 10.6667 0.333313C4.65144 0.333313 6.93768 0.333313 1.33341 0.333313Z" fill="#a3a3a3" /></svg> : value < 0 ? <svg width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.331 8L10.8573 6.47333L7.60476 3.22L4.93876 5.88667L0 0.94L0.939765 0L4.93876 4L7.60476 1.33333L11.8037 5.52667L13.33 4V8H9.331Z" fill="#D53033" /></svg> : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10.6667 4L12.1933 5.52667L8.94001 8.78L6.27334 6.11333L1.33334 11.06L2.27334 12L6.27334 8L8.94001 10.6667L13.14 6.47333L14.6667 8V4H10.6667Z" fill="#00A44B" /></svg>}</span>;
    $[0] = value;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2 || $[4] !== value) {
    t3 = <span className={t1}>{t2}{value}%</span>;
    $[2] = t1;
    $[3] = t2;
    $[4] = value;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span>since last week</span>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3) {
    t5 = <div className="flex flex-col text-[10px] text-neutral-400 text-center items-center flex-shrink-0">{t3}{t4}</div>;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
function fmtNotifDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      year: "numeric"
    });
  } catch {
    return "";
  }
}
function PipelineHotListBody(t0) {
  const $ = _c(7);
  const {
    drugs,
    onRowClick
  } = t0;
  if (!drugs?.length) {
    return null;
  }
  let t1;
  if ($[0] !== drugs || $[1] !== onRowClick) {
    let t2;
    if ($[3] !== onRowClick) {
      t2 = d => {
        const metaParts = [];
        if (d.sponsor) {
          metaParts.push(d.sponsorCount > 1 ? `${d.sponsor} +${d.sponsorCount - 1}` : d.sponsor);
        }
        if (d.totalEnrollment) {
          metaParts.push(`${d.totalEnrollment.toLocaleString()} patients`);
        }
        if (d.earliestPrimaryCompletion) {
          metaParts.push(`completes ${fmtNotifDate(d.earliestPrimaryCompletion)}`);
        }
        const oneLineParts = [...metaParts];
        if (d.mechanismPreview) {
          oneLineParts.push(d.mechanismPreview);
        }
        const oneLine = oneLineParts.join(" \xB7 ");
        return <div key={d.slug || d.rank} onClick={e => {
          e.stopPropagation();
          onRowClick(d);
        }} className="flex flex-col gap-1 p-2 rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 cursor-pointer"><div className="flex items-start justify-between gap-2"><div className="flex items-center gap-1.5 min-w-0 flex-1"><span className="text-[12px] sm:text-[13px] font-semibold text-black truncate">{d.rank ? `${d.rank}. ` : ""}{d.displayName}</span>{d.phase && <span className="inline-flex items-center text-[9px] font-semibold leading-none px-1.5 py-0.5 rounded uppercase tracking-wide bg-blue-50 text-blue-700 border border-blue-200 flex-shrink-0">{d.phase}</span>}</div><SinceLastWeekWidget pct={d.percentageChangeFromLastWeek} /></div><div className="text-[11px] text-neutral-700 font-medium truncate">{d.indication || "Indication pending"}</div>{oneLine && <div className="text-[10px] sm:text-[11px] text-neutral-500 truncate">{oneLine}</div>}</div>;
      };
      $[3] = onRowClick;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = drugs.map(t2);
    $[0] = drugs;
    $[1] = onRowClick;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className="mt-2 flex flex-col gap-1.5">{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function NotificationItem(t0) {
  const $ = _c(54);
  const {
    notification,
    onClick
  } = t0;
  const router = useRouter();
  const [, setLoading] = useState(false);
  const [hasCalledSocket, setHasCalledSocket] = useState(false);
  const {
    cancelAppointment,
    approveRescheduledAppointment
  } = useAppointments();
  const users = useSelector(_temp);
  const dispatch = useDispatch();
  let t1;
  if ($[0] !== approveRescheduledAppointment?.data || $[1] !== approveRescheduledAppointment?.error?.data?.message || $[2] !== approveRescheduledAppointment?.error?.status || $[3] !== approveRescheduledAppointment?.isError || $[4] !== approveRescheduledAppointment?.isLoading || $[5] !== approveRescheduledAppointment?.isSuccess || $[6] !== approveRescheduledAppointment?.status || $[7] !== dispatch || $[8] !== hasCalledSocket || $[9] !== notification || $[10] !== onClick || $[11] !== users) {
    t1 = () => {
      if (approveRescheduledAppointment?.isLoading) {
        setLoading(true);
      } else {
        if (!approveRescheduledAppointment?.isLoading && approveRescheduledAppointment?.isSuccess && approveRescheduledAppointment?.data && !hasCalledSocket) {
          const appointmentData = approveRescheduledAppointment?.data?.slot;
          socketService.confirmMetingByUser({
            senderId: users?._id,
            receiverId: appointmentData?.repId,
            profilePic: users?.profile,
            appointmentId: appointmentData?._id,
            senderName: users?.firstName + " " + users.lastName
          });
          dispatch(removeNotification({
            notificationId: notification?._id,
            type: "meeting"
          }));
          setHasCalledSocket(true);
          onClick(notification);
        } else {
          if (approveRescheduledAppointment?.isError && approveRescheduledAppointment?.status === "rejected" && approveRescheduledAppointment?.error?.status === 400) {
            setLoading(false);
            toast.error(approveRescheduledAppointment?.error?.data?.message);
          }
        }
      }
    };
    $[0] = approveRescheduledAppointment?.data;
    $[1] = approveRescheduledAppointment?.error?.data?.message;
    $[2] = approveRescheduledAppointment?.error?.status;
    $[3] = approveRescheduledAppointment?.isError;
    $[4] = approveRescheduledAppointment?.isLoading;
    $[5] = approveRescheduledAppointment?.isSuccess;
    $[6] = approveRescheduledAppointment?.status;
    $[7] = dispatch;
    $[8] = hasCalledSocket;
    $[9] = notification;
    $[10] = onClick;
    $[11] = users;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  let t2;
  if ($[13] !== approveRescheduledAppointment) {
    t2 = [approveRescheduledAppointment];
    $[13] = approveRescheduledAppointment;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  useEffect(t1, t2);
  let t3;
  if ($[15] !== cancelAppointment || $[16] !== dispatch || $[17] !== hasCalledSocket || $[18] !== notification || $[19] !== onClick || $[20] !== users) {
    t3 = () => {
      if (cancelAppointment?.isLoading) {
        setLoading(true);
      } else {
        if (!cancelAppointment?.isLoading && cancelAppointment?.isSuccess && cancelAppointment?.data && cancelAppointment && !hasCalledSocket) {
          const selectedAppointment_0 = notification?.appointmentId;
          socketService.cancelMetingByUser({
            senderId: users?._id,
            receiverId: selectedAppointment_0?.repId,
            profilePic: users?.profile,
            appointmentId: selectedAppointment_0?._id,
            senderName: users?.firstName + " " + users.lastName
          });
          dispatch(removeNotification({
            notificationId: notification?._id,
            type: "meeting"
          }));
          setHasCalledSocket(true);
          onClick(notification);
        } else {
          if (cancelAppointment?.isError && cancelAppointment?.status === "rejected" && cancelAppointment?.error?.status === 400) {
            setLoading(false);
            toast.error(cancelAppointment?.error?.data?.message);
          }
        }
      }
    };
    $[15] = cancelAppointment;
    $[16] = dispatch;
    $[17] = hasCalledSocket;
    $[18] = notification;
    $[19] = onClick;
    $[20] = users;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  let t4;
  if ($[22] !== cancelAppointment) {
    t4 = [cancelAppointment];
    $[22] = cancelAppointment;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  useEffect(t3, t4);
  let t5;
  if ($[24] !== notification || $[25] !== onClick) {
    t5 = () => onClick(notification);
    $[24] = notification;
    $[25] = onClick;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== notification?.title) {
    t6 = notification?.title?.split(":")?.length > 0 ? notification?.title?.split(":")[0] : notification?.title;
    $[27] = notification?.title;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  const t7 = notification?.type === "message" && notification?.senderId !== ALPHAMD_SENDER_ID ? "has sent you a message" : "";
  let t8;
  if ($[29] !== t7) {
    t8 = <span className="font-medium text-neutral-600">{t7}</span>;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== t5 || $[32] !== t6 || $[33] !== t8) {
    t9 = <div className="font-semibold text-[black] truncate" onClick={t5}>{t6}{" "}{t8}</div>;
    $[31] = t5;
    $[32] = t6;
    $[33] = t8;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== notification || $[36] !== onClick || $[37] !== router) {
    t10 = notification?.type === "pipeline_hot_list" && notification?.metadata?.drugs?.length ? <PipelineHotListBody drugs={notification.metadata.drugs} onRowClick={d => {
      onClick(notification);
      if (d?.href) {
        router.push(d.href);
      } else {
        if (d?.slug) {
          router.push(`/pipeline/${d.slug}`);
        }
      }
    }} /> : <div className="p-2 mt-2 w-full font-medium text-black rounded-md border border-solid border-neutral-200 break-words whitespace-pre-line overflow-hidden" onClick={() => onClick(notification)}>{notification?.description}</div>;
    $[35] = notification;
    $[36] = onClick;
    $[37] = router;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  let t11;
  if ($[39] !== t10 || $[40] !== t9) {
    t11 = <div className="flex flex-1 min-w-0 flex-col">{t9}{t10}</div>;
    $[39] = t10;
    $[40] = t9;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  let t12;
  if ($[42] !== notification?.isRead) {
    t12 = !notification?.isRead && <div className="flex-shrink-0"><div className="flex shrink-0 w-2 h-2 bg-blue-600 rounded-full" /></div>;
    $[42] = notification?.isRead;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  let t13;
  if ($[44] !== t11 || $[45] !== t12) {
    t13 = <div className="flex items-start gap-2 sm:gap-3">{t11}{t12}</div>;
    $[44] = t11;
    $[45] = t12;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  const t14 = notification?.createdAt;
  let t15;
  if ($[47] !== t14) {
    t15 = convertTo24HourWithDateFormat(t14);
    $[47] = t14;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  let t16;
  if ($[49] !== t15) {
    t16 = <div className="gap-2 self-stretch px-2 mt-2 w-full font-medium text-neutral-400">{t15}</div>;
    $[49] = t15;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] !== t13 || $[52] !== t16) {
    t17 = <article className="flex w-full cursor-pointer px-2 flex-col font-poppins text-xs py-2.5"><div className="flex flex-col border p-2 rounded-md">{t13}{t16}</div></article>;
    $[51] = t13;
    $[52] = t16;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  return t17;
}
function _temp(state) {
  return state?.userData?.userData?.user;
}
export default NotificationItem;